import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, TrendingUp, DollarSign, Calendar, ShieldCheck, Sparkles } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, no-auth viewer for a prospectus share link.
 * Route: /prospectus/share/:token
 *
 * Recipients (CFOs / sponsors) get a clean read-only view of the
 * business case. Two CTAs ("Schedule a walkthrough", "Reply with
 * questions") fire a /event call back to the backend, which fires
 * a one-shot Resend email + Slack/Teams nudge to the prospectus
 * owner. This is a public route — uses raw axios on purpose
 * (allowlisted in scripts/check_frontend_auth_calls.py).
 */
export default function ProspectusSharePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [ctaSubmitting, setCtaSubmitting] = useState(null);
  const [ctaDone, setCtaDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${BACKEND}/api/prospectus-share/${token}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) {
            setError("This share link is no longer active.");
          } else {
            setError("Could not load the prospectus. Please try again later.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const recordCta = async (label) => {
    setCtaSubmitting(label);
    try {
      await axios.post(`${BACKEND}/api/prospectus-share/${token}/event`, {
        kind: 'cta_click', cta_label: label,
      });
      setCtaDone(true);
    } catch {
      // We still mark done — the user took an action; we don't want to
      // re-trigger the email if the network blipped.
      setCtaDone(true);
    } finally {
      setCtaSubmitting(null);
    }
  };

  const prospectus = data?.prospectus;
  const share = data?.share;

  const ownerName = share?.owner_name;
  const recipientLabel = share?.recipient_label;

  const fmtMoney = useMemo(() => (n) => {
    if (n === null || n === undefined) return '—';
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Number(n).toLocaleString()}`;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading the business case…
        </div>
      </div>
    );
  }

  if (error || !prospectus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="prospectus-share-error">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-slate-600">{error || "This share link is no longer active."}</p>
        </div>
      </div>
    );
  }

  const roi = (prospectus.roi_projection || 0) / 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="prospectus-share-page">
      {/* Hero */}
      <header className="border-b bg-gradient-to-br from-indigo-700 via-indigo-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-200 mb-3">
            Business Case · {prospectus.organization_name || 'Confidential'}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-2" data-testid="share-title">
            {prospectus.title || prospectus.project_name || 'Business Case'}
          </h1>
          {prospectus.project_name && prospectus.project_name !== prospectus.title && (
            <p className="text-lg text-indigo-100">{prospectus.project_name}</p>
          )}
          {recipientLabel && (
            <p className="mt-4 text-sm text-indigo-200">
              Prepared for <strong>{recipientLabel}</strong>{ownerName ? ` by ${ownerName}` : ''}
            </p>
          )}
        </div>
      </header>

      {/* KPI tiles */}
      <section className="max-w-4xl mx-auto px-6 -mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="share-kpi-tiles">
        <KpiTile icon={DollarSign} label="Total investment" value={fmtMoney(prospectus.total_investment)} />
        <KpiTile icon={TrendingUp} label="ROI projection" value={`${roi.toFixed(2)}x`} accent />
        <KpiTile icon={Calendar} label="Payback" value={prospectus.payback_period_months ? `${prospectus.payback_period_months.toFixed(1)} mo` : '—'} />
        <KpiTile icon={ShieldCheck} label="Confidence" value={(prospectus.confidence_level || '—').toUpperCase()} />
      </section>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* Why this matters */}
        {prospectus.confidence_rationale && (
          <Section title="Why this case is credible">
            <p className="text-slate-700 leading-relaxed">{prospectus.confidence_rationale}</p>
          </Section>
        )}

        {/* KPIs */}
        {prospectus.target_kpis?.length > 0 && (
          <Section title="What we're moving">
            <ul className="space-y-3">
              {prospectus.target_kpis.map((kpi, i) => (
                <li key={kpi.kpi_id || `${kpi.kpi_name}-${i}`} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between gap-4">
                    <p className="font-semibold text-slate-900">{kpi.kpi_name}</p>
                    <span className="text-sm font-bold text-indigo-700">
                      +{kpi.target_improvement_percent}%
                    </span>
                  </div>
                  {kpi.importance && (
                    <p className="mt-2 text-sm text-slate-600">{kpi.importance}</p>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Internal evidence */}
        {prospectus.internal_evidence?.length > 0 && (
          <Section title="Backing evidence">
            <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
              {prospectus.internal_evidence.map((e, i) => <li key={`evidence-${i}-${(e || '').slice(0, 16)}`}>{e}</li>)}
            </ul>
          </Section>
        )}

        {/* CTAs */}
        <Section title={ctaDone ? `Thanks — ${ownerName || 'we'} will be in touch shortly.` : 'Next step'}>
          {ctaDone ? (
            <p className="text-slate-700">
              We notified the case owner that you're interested. Expect a personal note within the day.
            </p>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => recordCta('Schedule a walkthrough')}
                disabled={ctaSubmitting !== null}
                className="bg-indigo-700 hover:bg-indigo-800 disabled:opacity-60 text-white font-semibold rounded-lg px-5 py-3 inline-flex items-center justify-center gap-2"
                data-testid="share-cta-walkthrough"
              >
                {ctaSubmitting === 'Schedule a walkthrough' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Schedule a walkthrough
              </button>
              <button
                type="button"
                onClick={() => recordCta('Reply with questions')}
                disabled={ctaSubmitting !== null}
                className="border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-60 font-semibold rounded-lg px-5 py-3 inline-flex items-center justify-center gap-2"
                data-testid="share-cta-reply"
              >
                Reply with questions
              </button>
            </div>
          )}
        </Section>
      </main>

      <footer className="border-t bg-slate-50 py-6 text-center text-xs text-slate-500">
        Powered by Seertech · This view is read-only.
      </footer>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${accent ? 'border-indigo-300 shadow-indigo-100' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider mb-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className={`text-2xl font-bold ${accent ? 'text-indigo-700' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-slate-900 mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
